import { useCallback, useRef, useState } from 'react';
import { showAlert } from '@/utils/common/alert';
import { extractErrorMessage } from '@/utils/common/errorHandling';
import {
  categorizeError,
  shouldAllowRetry,
  ErrorType,
} from '@/utils/admin/errorHandling';

const DEFAULT_MAX_RETRIES = 2;
const BASE_DELAY_MS = 1000;

function isRetryableError(error: unknown): boolean {
  const errorType = categorizeError(error);
  return shouldAllowRetry(error, errorType);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface ExecuteOptions<TResult> {
  onSuccess?: (result: TResult) => void;
  onError?: (message: string, originalError?: unknown) => void;
  /** Suppress the default alert/toast on error */
  silent?: boolean;
}

/**
 * Wraps an RTK Query mutation trigger with retry logic and user-friendly error
 * handling. Returns a stable `execute` function that can replace raw try/catch
 * blocks throughout the corkboard hooks.
 *
 * Retryable errors (network, 5xx, timeout, 429) are retried up to
 * `maxRetries` times with exponential back-off. Non-retryable errors (4xx
 * client errors, auth errors) fail immediately.
 */
export function useCorkBoardMutation<TArg, TResult>(
  mutationTrigger: (arg: TArg) => { unwrap: () => Promise<TResult> },
  options: {
    successMessage?: string;
    errorFallback?: string;
    maxRetries?: number;
  } = {},
) {
  const {
    successMessage,
    errorFallback = 'Something went wrong. Please try again.',
    maxRetries = DEFAULT_MAX_RETRIES,
  } = options;

  const [isRetrying, setIsRetrying] = useState(false);
  const abortRef = useRef(false);

  const execute = useCallback(
    async (
      arg: TArg,
      executeOptions?: ExecuteOptions<TResult>,
    ): Promise<{ success: true; data: TResult } | { success: false; error: string; originalError?: unknown }> => {
      abortRef.current = false;
      let lastError: unknown;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (abortRef.current) break;

        if (attempt > 0) {
          setIsRetrying(true);
          await delay(BASE_DELAY_MS * Math.pow(2, attempt - 1));
          if (abortRef.current) break;
        }

        try {
          const result = await mutationTrigger(arg).unwrap();

          setIsRetrying(false);

          if (successMessage && !executeOptions?.silent) {
            showAlert('Success', successMessage);
          }
          executeOptions?.onSuccess?.(result);
          return { success: true, data: result };
        } catch (err: unknown) {
          lastError = err;

          const canRetry = attempt < maxRetries && isRetryableError(err);
          if (!canRetry) break;
        }
      }

      setIsRetrying(false);

      const message = extractErrorMessage(lastError, errorFallback);

      if (!executeOptions?.silent) {
        showAlert('Error', message);
      }
      executeOptions?.onError?.(message, lastError);
      return { success: false, error: message, originalError: lastError };
    },
    [mutationTrigger, successMessage, errorFallback, maxRetries],
  );

  return { execute, isRetrying } as const;
}
